import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useCreateEventContext } from '../../../../context/CreateEventContext';
import { useAuth } from '../../../../context/AuthContext';
import FloorPlanMiniMap from '../../../EO/ViewEventDetails/FloorPlan/FloorPlanMiniMap';
import { formatDateIntoString, convertTo12HourFormat } from '../../../../utils/dateAndTime';
import {
  LocationMarkerIcon,
  CalendarIcon,
  ClockIcon,
  DownloadIcon,
  CheckCircleIcon,
} from '@heroicons/react/outline';

const CHECKLIST = [
  'Bring your business license or permit if required',
  'Arrive 30–60 min before event start for setup',
  'Have your booth number ready at check-in',
  'Bring change and a card reader if accepting payments',
  'Bring your own table, tent, and display materials unless provided',
  'Stay until the event closes — early teardown may affect future applications',
];

const MyBooth = () => {
  const { eventData } = useCreateEventContext();
  const { user } = useAuth();
  const qrRef = useRef(null);

  const applicationId = eventData?.applicationId || eventData?.id || '';
  const boothNumber = eventData?.assignedBooth;
  const hasBoothAssigned = boothNumber && boothNumber !== '-' && boothNumber !== '';

  // QR code value — encodes applicationId for EO check-in scanner
  const qrValue = JSON.stringify({
    applicationId,
    eventId: eventData?.eventId || eventData?.id || '',
    type: 'vendor-checkin',
  });

  const handleDownloadQR = () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pebble-checkin-${applicationId}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full px-4 sm:px-8 py-6 space-y-6">
      {/* Booth number banner */}
      <div className={`rounded-xl p-6 border-2 ${
        hasBoothAssigned
          ? 'bg-green-50 border-green-200'
          : 'bg-amber-50 border-amber-200'
      }`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1">
          Assigned Booth
        </p>
        {hasBoothAssigned ? (
          <div className="flex items-baseline gap-3">
            <p className="text-5xl font-black text-grey-900">{boothNumber}</p>
            <div className="flex items-center gap-1 text-green-700">
              <CheckCircleIcon className="w-4 h-4" />
              <span className="text-xs font-semibold">Confirmed</span>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-2xl font-bold text-amber-700">Pending Assignment</p>
            <p className="text-sm text-amber-600 mt-1">
              Your organizer will assign your booth before the event. Check back here or watch for an email.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* QR check-in code */}
        <div className="bg-white rounded-xl border border-grey-200 p-6 flex flex-col items-center gap-4">
          <div>
            <p className="text-sm font-semibold text-grey-900 text-center">Check-in QR Code</p>
            <p className="text-xs text-grey-500 text-center mt-0.5">Show this to the organizer at the gate</p>
          </div>
          <div ref={qrRef} className="p-3 bg-white rounded-lg border border-grey-100 shadow-sm">
            <QRCodeSVG
              value={qrValue}
              size={180}
              level="M"
              includeMargin={false}
            />
          </div>
          <button
            onClick={handleDownloadQR}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-grey-700 border border-grey-300 rounded-lg hover:bg-grey-50 transition"
          >
            <DownloadIcon className="w-3.5 h-3.5" />
            Save QR Code
          </button>
        </div>

        {/* Event day details */}
        <div className="bg-white rounded-xl border border-grey-200 p-6 space-y-4">
          <p className="text-sm font-semibold text-grey-900">Event Day Details</p>
          <div className="space-y-3">
            {eventData?.startDate && (
              <div className="flex items-start gap-3">
                <CalendarIcon className="w-4 h-4 text-grey-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-grey-500 font-medium">Date</p>
                  <p className="text-sm font-semibold text-grey-800">
                    {formatDateIntoString(eventData.startDate)}
                  </p>
                </div>
              </div>
            )}
            {eventData?.startTime && (
              <div className="flex items-start gap-3">
                <ClockIcon className="w-4 h-4 text-grey-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-grey-500 font-medium">Hours</p>
                  <p className="text-sm font-semibold text-grey-800">
                    {convertTo12HourFormat(eventData.startTime)}
                    {eventData?.endTime && ` – ${convertTo12HourFormat(eventData.endTime)}`}
                  </p>
                  <p className="text-xs text-grey-500 mt-0.5">Arrive 30–60 min early for setup</p>
                </div>
              </div>
            )}
            {eventData?.location && (
              <div className="flex items-start gap-3">
                <LocationMarkerIcon className="w-4 h-4 text-grey-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-grey-500 font-medium">Location</p>
                  <p className="text-sm font-semibold text-grey-800">{eventData.location}</p>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(eventData.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Open in Maps
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Venue mini-map */}
      <FloorPlanMiniMap
        eventId={eventData?.eventId || eventData?.id}
        vendorUserId={user?.uid}
      />

      {/* Vendor checklist */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <p className="text-sm font-semibold text-grey-900 mb-4">Vendor Prep Checklist</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CHECKLIST.map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <p className="text-sm text-grey-700">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyBooth;
